/**
 * Endereco de entrega em campos separados.
 *
 * Aqui mora a regra do endereco da corrida, longe do React para poder ser
 * testada. A linha montada por `linhaLalamove` e a mesma que o backend monta em
 * App\Services\EnderecoEntrega::linha() — a tela mostra ao operador exatamente
 * o texto que o motoboy vai ler. Se um dos dois mudar, o outro muda junto.
 *
 * O que originou isto: o endereco viajava como uma string so, remontada a
 * partir do rotulo do geocoder. O geocoder so devolve o numero quando o
 * provedor conhece o imovel, entao o numero da casa sumia calado. Em
 * 20/09/2026, 7 das 11 corridas registradas tinham ido sem numero.
 */

export const DESTINO_VAZIO = {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    referencia: '',
    lat: null,
    lng: null,
    precisao: null,
    semNumero: false,
    numeroConfirmado: false,
    enderecoConfirmado: false,
    divergenciaAssumida: false,
};

/** Precisoes em que o ponto nao aponta para um imovel. */
const PRECISAO_FRACA = ['bairro', 'cidade', 'aproximada'];

const SEM_NUMERO = ['sn', 'snumero', 'semnumero', '0', '00', '000'];

/**
 * Digitos, letras e os separadores que aparecem em numero de casa.
 * A virgula entra porque o cadastro tem "QD 300, Lote 25" e "172,A" de verdade.
 */
const NUMERO_VALIDO = /^[\p{L}\p{N}\s,/\-.º°]+$/u;

/** Prefixos que nao ajudam a comparar duas ruas. */
const TIPOS_DE_VIA = /^(r|rua|av|avenida|al|alameda|tv|travessa|pca|praca|rod|rodovia|estr|estrada|via|lgo|largo)\.?\s+/;

export const soDigitos = (v) => String(v ?? '').replace(/\D/g, '');

export const limpar = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

export const ehSemNumero = (v) => {
    const n = String(v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return n !== '' && SEM_NUMERO.includes(n);
};

export const formatarCep = (v) => {
    const d = soDigitos(v).slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** Sem acento, sem caixa, sem tipo de via: o que sobra e o nome da rua. */
export const normalizarVia = (v) => limpar(v)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(TIPOS_DE_VIA, '')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();

export const mesmaRua = (a, b) => {
    const x = normalizarVia(a);
    const y = normalizarVia(b);
    if (!x || !y) return true;
    return x === y || x.includes(y) || y.includes(x);
};

/**
 * A linha que vai em stops[].address.
 *
 * Formato dos Correios, que e o que geocoder e motorista leem melhor. O
 * complemento fica de fora de proposito: a propria Lalamove manda por
 * apartamento e bloco em remarks, e complemento no meio atrapalha o geocoding.
 */
export const linhaLalamove = (d = {}) => {
    const rua = limpar(d.logradouro) || limpar(d.bairro);
    const numero = limpar(d.numero);
    const bairro = limpar(d.bairro);
    const cidade = limpar(d.cidade);
    const uf = limpar(d.uf).toUpperCase();
    const cep = soDigitos(d.cep);

    let primeira = rua;
    if (numero) primeira += `, ${numero}`;
    else if (d.semNumero) primeira += ', s/n';
    if (bairro && bairro !== rua) primeira += ` - ${bairro}`;

    const partes = [
        primeira.replace(/^[\s,-]+|[\s,-]+$/g, ''),
        cidade ? `${cidade}${uf ? ` - ${uf}` : ''}` : uf,
        cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep,
    ].filter(Boolean);

    return partes.join(', ');
};

/** O que o motorista le no app e nao cabe no endereco. */
export const observacaoDoMotorista = (d = {}) => [
    limpar(d.complemento) ? `Complemento: ${limpar(d.complemento)}` : null,
    limpar(d.referencia) ? `Referencia: ${limpar(d.referencia)}` : null,
].filter(Boolean).join(' | ') || null;

export const temPonto = (d = {}) => d.lat !== null && d.lat !== undefined && d.lng !== null && d.lng !== undefined;

/** Compara so os digitos: "1203" e "1203 A" sao a mesma casa. */
export const numeroConfere = (informado, cadastrado) => {
    const c = soDigitos(cadastrado);
    if (!c || ehSemNumero(cadastrado)) return true;
    return c === soDigitos(informado);
};

/**
 * Tudo que impede a corrida de sair, em portugues de operador.
 *
 * Espelha App\Services\EnderecoEntrega::problemas(). A tela barra antes para o
 * operador nao descobrir o problema so depois de cotar; o servidor barra de
 * novo porque a checagem nao pode depender de a tela ter feito a parte dela.
 */
export const problemasDoDestino = (d = {}, cadastro = null) => {
    const p = [];
    const erro = (campo, mensagem) => p.push({ campo, nivel: 'erro', mensagem });
    const aviso = (campo, mensagem) => p.push({ campo, nivel: 'aviso', mensagem });

    if (!limpar(d.logradouro)) erro('logradouro', 'Informe a rua ou avenida da entrega.');
    if (!limpar(d.numero) && !d.semNumero) erro('numero', 'Falta o número da casa. Se o local não tem número, marque "sem número".');
    if (limpar(d.numero) && !/\d/.test(d.numero)) erro('numero', 'O número da casa precisa ter pelo menos um dígito.');
    // número de casa é dígito, às vezes com letra ou tracinho; o resto é lixo colado sem querer
    if (limpar(d.numero) && !NUMERO_VALIDO.test(limpar(d.numero))) erro('numero', 'O número da casa tem caracteres que não valem. Use só números e letras.');
    if (!limpar(d.cidade)) erro('cidade', 'Informe a cidade da entrega.');
    if (!limpar(d.uf)) erro('uf', 'Informe o estado (UF).');
    else if (limpar(d.uf).length !== 2) erro('uf', 'A UF tem duas letras, como SP.');
    if (!temPonto(d)) erro('ponto', 'Marque o ponto no mapa: é nele que o motoboy vai parar.');

    const cep = soDigitos(d.cep);
    if (cep && cep.length !== 8) aviso('cep', 'CEP fora do formato de 8 dígitos.');

    if (cadastro) {
        const cadastrado = limpar(cadastro.numero);
        const nivel = d.numeroConfirmado ? aviso : erro;

        if (!numeroConfere(d.numero, cadastrado)) {
            nivel('numero', `O número ${limpar(d.numero) || 's/n'} não é o que está na venda (${cadastrado || 's/n'}).`);
        }
        if (d.semNumero && cadastrado && !ehSemNumero(cadastrado)) {
            nivel('numero', `A venda tem o número ${cadastrado} cadastrado.`);
        }

        const troca = trocaDeEndereco(d, cadastro);
        if (troca.rua || troca.cidade) {
            (d.enderecoConfirmado ? aviso : erro)(
                'logradouro',
                `O endereço não é o da venda (${troca.ruaCadastrada || '-'}, ${troca.cidadeCadastrada || '-'}, CEP ${formatarCep(troca.cepCadastrado) || '-'}).`,
            );
        } else if (troca.cep) {
            aviso('cep', `O CEP não é o da venda (${formatarCep(troca.cepCadastrado)}).`);
        }
    }

    if (d.precisao && PRECISAO_FRACA.includes(d.precisao)) {
        erro('ponto', 'O mapa não achou este número. Arraste o pino até a porta antes de confirmar.');
    }

    // Regra que so existe na tela, de proposito. Ponto que nao aponta para o
    // imovel — o mapa achou so a rua, ou o pino veio de uma tentativa anterior —
    // pode estar a duas quadras da porta. O servidor nao tem como saber se
    // alguem olhou; quem olha e quem esta vendo o mapa.
    if (temPonto(d) && d.precisao !== 'exata' && !d.pontoConfirmado) {
        erro('ponto', 'Confira onde o pino caiu no mapa e confirme antes de seguir.');
    }

    return p;
};

export const trocaDeEndereco = (d = {}, cadastro = null) => {
    const ruaCadastrada = limpar(cadastro?.endereco ?? cadastro?.logradouro);
    const cidadeCadastrada = limpar(cadastro?.cidade);
    const cepCadastrado = soDigitos(cadastro?.cep);
    const cep = soDigitos(d.cep);

    return {
        ruaCadastrada,
        cidadeCadastrada,
        cepCadastrado,
        rua: Boolean(cadastro) && Boolean(limpar(d.logradouro)) && Boolean(ruaCadastrada) && !mesmaRua(d.logradouro, ruaCadastrada),
        cidade: Boolean(cadastro) && Boolean(limpar(d.cidade)) && Boolean(cidadeCadastrada) && !mesmaRua(d.cidade, cidadeCadastrada),
        cep: Boolean(cadastro) && cep.length === 8 && cepCadastrado.length === 8 && cep !== cepCadastrado,
    };
};

export const impedimentos = (d, cadastro) => problemasDoDestino(d, cadastro)
    .filter((x) => x.nivel === 'erro')
    .map((x) => x.mensagem);

export const campoComErro = (d, cadastro) => {
    const mapa = {};
    problemasDoDestino(d, cadastro).forEach((x) => {
        if (x.nivel === 'erro' && !mapa[x.campo]) mapa[x.campo] = x.mensagem;
    });
    return mapa;
};

/**
 * Endereco vindo do backend, seja o cadastro da venda (`endereco`) ou o destino
 * ja montado (`destino`). Os dois chegam com os mesmos campos em snake_case.
 */
export const doCadastro = (e = {}) => ({
    ...DESTINO_VAZIO,
    logradouro: limpar(e.logradouro ?? e.endereco),
    numero: ehSemNumero(e.numero) ? '' : limpar(e.numero),
    complemento: limpar(e.complemento),
    bairro: limpar(e.bairro),
    cidade: limpar(e.cidade),
    uf: limpar(e.uf).toUpperCase().slice(0, 2),
    cep: soDigitos(e.cep),
    referencia: limpar(e.referencia),
    lat: e.lat ?? null,
    lng: e.lng ?? null,
    precisao: e.precisao ?? null,
    semNumero: Boolean(e.sem_numero) || ehSemNumero(e.numero),
    // Ponto herdado de tentativa anterior nao vem conferido: as corridas
    // antigas foram cotadas com o fluxo que errava o endereco, entao o pino
    // delas nao vale como confirmacao.
    pontoConfirmado: false,
});

/**
 * Aplica o que o geocoder achou sem apagar o que o operador ja tem.
 *
 * Dois modos, porque as duas origens nao merecem a mesma confianca:
 *
 * - 'substituir': o operador escolheu aquela sugestao na lista. E escolha
 *   deliberada, entao rua, bairro, cidade, UF e CEP passam a ser os dela.
 * - 'completar': veio da geocodificacao reversa de um pino arrastado. Ninguem
 *   pediu para trocar o endereco, so para mover o ponto — entao isto apenas
 *   preenche campo vazio e nao encosta no que ja esta escrito.
 *
 * O modo 'completar' existe por causa de um caso real: o reverso do ponto da
 * Rua Barra Longa 54 devolve bairro "Parque Continental" e CEP 05332-000,
 * enquanto a venda diz "Jaguare" e 05330-040. Sobrescrever trocaria o endereco
 * do cliente pelo palpite do mapa.
 *
 * Numero e complemento nunca vem do geocoder por cima: so preenchem vazio.
 */
export const aplicarGeocode = (atual, componentes = {}, extras = {}, modo = 'substituir') => {
    const preferido = modo === 'substituir'
        ? (novo, velho) => (limpar(novo) ? limpar(novo) : velho)
        : (novo, velho) => (limpar(velho) ? velho : limpar(novo));

    const cepNovo = soDigitos(componentes.cep);

    return {
        ...atual,
        logradouro: preferido(componentes.logradouro, atual.logradouro),
        bairro: preferido(componentes.bairro, atual.bairro),
        cidade: preferido(componentes.cidade, atual.cidade),
        uf: (preferido(componentes.uf, atual.uf) || '').toUpperCase().slice(0, 2),
        cep: modo === 'substituir' ? (cepNovo || atual.cep) : (atual.cep || cepNovo),
        // O numero do cadastro manda sempre. O do geocoder so preenche vazio.
        numero: limpar(atual.numero) || limpar(componentes.numero) || '',
        ...extras,
    };
};

/**
 * O geocoder discorda do que esta escrito?
 *
 * Endereco textual e coordenada apontando para ruas diferentes e o jeito mais
 * silencioso de entregar no lugar errado: o texto convence o operador, a
 * coordenada leva o motoboy. Quando isso acontece, quem decide e o operador.
 */
export const divergencia = (destino, componentes) => {
    if (!componentes) return null;

    const ruaNova = limpar(componentes.logradouro);
    const ruaAtual = limpar(destino.logradouro);

    // So a rua dispara a pergunta.
    //
    // Medido em 20/09/2026: mover o pino 25 metros na mesma Avenida Paulista
    // muda o bairro (Morro dos Ingleses -> Cerqueira Cesar) e o CEP
    // (01310-200 -> 01311-200). Perguntar nisso seria pop-up a cada ajuste de
    // pino, e operador que clica em "usar" no automatico acabaria trocando o
    // endereco do cliente por palpite do mapa.
    if (!ruaNova || !ruaAtual || mesmaRua(ruaAtual, ruaNova)) return null;

    const diferente = (novo, atual) => Boolean(limpar(novo))
        && limpar(novo).toLowerCase() !== limpar(atual).toLowerCase();

    const campos = ['logradouro'];
    if (diferente(componentes.bairro, destino.bairro)) campos.push('bairro');
    if (diferente(componentes.cidade, destino.cidade)) campos.push('cidade');
    if (soDigitos(componentes.cep) && soDigitos(componentes.cep) !== soDigitos(destino.cep)) campos.push('cep');

    return {
        campos,
        componentes,
        // O numero fica de fora de proposito: o reverso devolve o imovel mais
        // proximo que ele conhece (1640 para quem esta em frente ao 1578), e
        // trocar o numero do cliente por esse palpite e exatamente o bug que
        // este componente existe para impedir.
        numeroPreservado: limpar(destino.numero) || null,
        linha: linhaLalamove({
            logradouro: componentes.logradouro,
            numero: destino.numero,
            bairro: componentes.bairro || destino.bairro,
            cidade: componentes.cidade || destino.cidade,
            uf: componentes.uf || destino.uf,
            cep: componentes.cep || destino.cep,
        }),
    };
};

/** Rotulos dos campos, para a tela nao ter de repetir esse de-para. */
export const ROTULO_CAMPO = {
    logradouro: 'Rua / avenida',
    numero: 'Número',
    bairro: 'Bairro',
    cidade: 'Cidade',
    uf: 'UF',
    cep: 'CEP',
};

/** O corpo que o backend espera em `destino`. */
export const paraPayload = (d = {}) => ({
    logradouro: limpar(d.logradouro) || null,
    numero: d.semNumero ? null : (limpar(d.numero) || null),
    complemento: limpar(d.complemento) || null,
    bairro: limpar(d.bairro) || null,
    cidade: limpar(d.cidade) || null,
    uf: limpar(d.uf).toUpperCase().slice(0, 2) || null,
    cep: soDigitos(d.cep) || null,
    referencia: limpar(d.referencia) || null,
    lat: d.lat ?? null,
    lng: d.lng ?? null,
    precisao: d.precisao || null,
    sem_numero: Boolean(d.semNumero),
    numero_confirmado: Boolean(d.numeroConfirmado),
    endereco_confirmado: Boolean(d.enderecoConfirmado),
    // o operador assumiu que a rua do ponto no mapa nao bate com o texto
    divergencia_assumida: Boolean(d.divergenciaAssumida),
});

export const PRECISAO = {
    exata: { rotulo: 'Número confirmado no mapa', cor: 'green' },
    rua: { rotulo: 'Rua confirmada, número não', cor: 'yellow' },
    aproximada: { rotulo: 'Ponto aproximado do CEP', cor: 'orange' },
    bairro: { rotulo: 'Só o bairro', cor: 'red' },
    cidade: { rotulo: 'Só a cidade', cor: 'red' },
    manual: { rotulo: 'Pino ajustado à mão', cor: 'purple' },
};
